import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeartIcon, BrainIcon, SparklesIcon, SunIcon, CalendarIcon, Users, ArrowRightIcon, MenuIcon, XIcon, MoonIcon } from 'lucide-react';
const HERO_MOUNTAIN_IMAGE = 'https://images.unsplash.com/photo-1568622673002-bf17add321d3?w=1920&q=80';
// ═══════════════════════════════════════════════════════════
// 🔗 REGISTRACIJOS NUORODA — čia įrašykite savo anketos URL
// ═══════════════════════════════════════════════════════════
const REGISTRATION_URL = 'https://example.com/registracija';
export function LandingPage({
  mode,
  toggleMode
}: {
  mode: 'light' | 'dark';
  toggleMode: () => void;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const fadeInUp = {
    initial: {
      opacity: 0,
      y: 30
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.65,
      ease: 'easeOut'
    }
  };
  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.1
      }
    },
    viewport: {
      once: true,
      margin: '-80px'
    }
  };
  const accentText = 'DFB Metodika:';
  const charVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.045,
        duration: 0.4,
        ease: 'easeOut'
      }
    })
  };
  const headlineVariants = {
    hidden: {
      opacity: 0,
      y: 24,
      filter: 'blur(12px)'
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.9,
        ease: 'easeOut',
        delay: 0.3
      }
    }
  };
  const subVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
        delay: 0.9
      }
    }
  };
  const ctaVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        delay: 1.4
      }
    }
  };
  // Reusable section label
  const SectionLabel = ({
    text,
    light = false
  }: {
    text: string;
    light?: boolean;
  }) => <div className="flex flex-col items-center mb-5" data-id="element-2">
      <p className={`text-xs uppercase tracking-widest font-semibold mb-3 ${light ? 'text-gold-light/70' : 'text-gold-dark'}`} data-id="element-3">
        {text}
      </p>
      <div className="w-10 h-0.5 bg-gold" data-id="element-4" />
    </div>;
  return <main className="w-full bg-sand-lightest transition-colors duration-300" data-id="element-5">
      {/* ── STICKY NAV ── */}
      <motion.nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-500" style={{
      background: scrolled ? 'rgba(var(--mountain-darker), 0.96)' : 'transparent',
      backdropFilter: scrolled ? 'blur(12px)' : 'none',
      boxShadow: scrolled ? '0 1px 0 rgba(255,255,255,0.06)' : 'none'
    }} initial={{
      y: -80,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.6,
      ease: 'easeOut',
      delay: 2.0
    }} data-id="element-6">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-7">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2 select-none" data-id="element-8">
            <span className="font-accent text-3xl tracking-wide text-[#F72585]" data-id="element-9">
              medituoti lengva
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8" data-id="element-10">
            {[{
            label: 'Apie',
            href: '#apie'
          }, {
            label: 'Metodika',
            href: '#metodika'
          }, {
            label: 'Programa',
            href: '#programa'
          }, {
            label: 'Komanda',
            href: '#komanda'
          }, {
            label: 'Tvarkaraštis',
            href: '#tvarkarastis'
          }].map(link => <a key={link.label} href={link.href} className="text-white/70 hover:text-white text-sm font-medium uppercase tracking-wider transition-colors duration-200" data-id="element-11">
                {link.label}
              </a>)}
          </div>

          {/* CTA & Dark Mode */}
          <div className="hidden md:flex items-center gap-4" data-id="element-12">
            <button onClick={toggleMode} className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Toggle dark mode" data-id="element-13">
              {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-14" /> : <SunIcon className="w-5 h-5" data-id="element-15" />}
            </button>
            <motion.a href={REGISTRATION_URL} target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.04
          }} whileTap={{
            scale: 0.97
          }} className="hover:bg-gold-dark text-mountain-darker px-6 py-2.5 rounded-full text-sm font-semibold transition-colors shadow-lg bg-[#F72585]" data-id="element-16">
              Registruotis
            </motion.a>
          </div>

          {/* Mobile burger & Dark Mode */}
          <div className="flex items-center gap-2 md:hidden" data-id="element-17">
            <button onClick={toggleMode} className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Toggle dark mode" data-id="element-18">
              {mode === 'light' ? <MoonIcon className="w-5 h-5" data-id="element-19" /> : <SunIcon className="w-5 h-5" data-id="element-20" />}
            </button>
            <button className="text-white/80 hover:text-white p-1" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Meniu" data-id="element-21">
              {mobileOpen ? <XIcon className="w-6 h-6" data-id="element-22" /> : <MenuIcon className="w-6 h-6" data-id="element-23" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence data-id="element-24">
          {mobileOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3
        }} className="md:hidden overflow-hidden" style={{
          background: 'rgba(var(--mountain-darker), 0.98)'
        }} data-id="element-25">
              <div className="px-6 py-4 flex flex-col gap-4 border-t border-white/10" data-id="element-26">
                {[{
              label: 'Apie',
              href: '#apie'
            }, {
              label: 'Metodika',
              href: '#metodika'
            }, {
              label: 'Programa',
              href: '#programa'
            }, {
              label: 'Komanda',
              href: '#komanda'
            }, {
              label: 'Tvarkaraštis',
              href: '#tvarkarastis'
            }].map(link => <a key={link.label} href={link.href} onClick={() => setMobileOpen(false)} className="text-white/80 hover:text-white text-base font-medium uppercase tracking-wider py-1 transition-colors" data-id="element-27">
                    {link.label}
                  </a>)}
                <a href={REGISTRATION_URL} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)} className="bg-gold text-mountain-darker px-6 py-3 rounded-full text-sm font-semibold text-center mt-2" data-id="element-28">
                  Registruotis
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </motion.nav>

      {/* ── HERO ── */}
      <section className="relative h-screen min-h-[640px] flex flex-col items-center justify-center px-4 overflow-hidden" data-id="element-29">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: `url('${HERO_MOUNTAIN_IMAGE}')`
      }} data-id="element-30" />

        <div className="relative z-20 max-w-4xl mx-auto text-center flex flex-col items-center" data-id="element-31">
          <motion.p className="text-sm font-accent text-gold-light mb-5 tracking-widest uppercase" initial="hidden" animate="visible" aria-label={accentText} data-id="element-32">
            {accentText.split('').filter((_, i) => i !== 0 && i !== 1 && i !== 2 && i !== 4 && i !== 5 && i !== 6 && i !== 7 && i !== 8 && i !== 9 && i !== 10 && i !== 11 && i !== 12).map((char, i) => <motion.span key={i} custom={i} variants={charVariants} style={{
            display: char === ' ' ? 'inline' : 'inline-block'
          }} data-id="element-33">
                  {char === ' ' ? '\u00A0' : char}
                </motion.span>)}
          </motion.p>

          <motion.h1 className="text-4xl md:text-6xl lg:text-7xl mb-6 leading-tight text-[#FFFFFF] bg-[#FFFFFF00] font-[600]" variants={headlineVariants} initial="hidden" animate="visible" data-id="element-34">
            Tavo Dėmesys Yra Brangiausias Tavo Resursas
          </motion.h1>

          <motion.p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed font-[300]" variants={subVariants} initial="hidden" animate="visible" data-id="element-35">
            Tavo energija keliauja ten, kur nukreipi savo fokusą. Treniruok
            dėmesį ir stebėk, kaip keičiasi tavo vidinė būsena ir produktyvumas.
          </motion.p>

          <motion.button variants={ctaVariants} initial="hidden" animate="visible" whileHover={{
          scale: 1.05,
          boxShadow: '0 20px 60px -10px rgba(0,0,0,0.4)'
        }} whileTap={{
          scale: 0.97
        }} className="bg-white hover:bg-gray-100 text-gray-900 px-14 py-[18px] rounded-full text-lg font-semibold shadow-2xl transition-colors" data-id="element-36">
            Sužinok Daugiau
          </motion.button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-30" data-id="element-37">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" data-id="element-38">
            <path d="M0 0C240 80 480 80 720 40C960 0 1200 0 1440 40V120H0V0Z" style={{
            fill: 'rgb(var(--sand-light))'
          }} data-id="element-39" />
          </svg>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section id="apie" className="py-20 px-4 bg-sand-light" data-id="element-40">
        <motion.div className="max-w-3xl mx-auto text-center" {...fadeInUp} data-id="element-41">
          <p className="text-lg md:text-xl text-mountain-dark leading-relaxed font-[300]" data-id="element-42">
            Šis kursas pakeis tavo požiūrį į meditaciją, transformuodamas ją į
            sąmoningą dėmesio valdymą. Užuot tik bandęs sugrįžti į „čia ir
            dabar", išmoksi tikslingai nukreipti savo dėmesį taip, kad
            reguliuotum savo vidinę būseną. Būtent čia ir slepiasi tavo laisvė.
          </p>
        </motion.div>
      </section>

      {/* ── CHALLENGES ── */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-43">
        <div className="max-w-6xl mx-auto" data-id="element-44">
          <motion.div className="mb-12" {...fadeInUp} data-id="element-45">
            <p className="font-accent italic text-gold-dark mb-4 text-[28px]" data-id="element-46">
              Visiems Pažįstami
            </p>
            <div className="h-[2px] w-[160px] mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-47" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-48">
              Kasdieniai Iššūkiai
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-49">
            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-50">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1537210249814-b9a10a161ae4?w=600&q=80')"
            }} data-id="element-51" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-52" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-53">
                01
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-54">
                <p className="text-lg font-bold text-white mb-1" data-id="element-55">
                  Nuolatinis stresas
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-56">
                  Kaip efektyviai spręsti problemas neprarandant vidinės
                  ramybės?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-57">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://cdn.magicpatterns.com/uploads/fkDNhwZwL2Fman3fp7TuCW/moritz-kindler-n6XW1P6a-_U-unsplash.jpg')"
            }} data-id="element-58" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-59" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-60">
                02
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-61">
                <p className="text-lg font-bold text-white mb-1" data-id="element-62">
                  Dėmesio blaškymasis
                </p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-63">
                  Kaip valdyti dėmesį nuolat besikeičiančioje aplinkoje?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-64">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1669369172813-938c5aaa8685?w=600&q=80')"
            }} data-id="element-65" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-66" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-67">
                03
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-68">
                <p className="text-lg font-bold text-white mb-1" data-id="element-69">Perdegimas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-70">
                  Kaip siekti ilgalaikių tikslų neperdegant?
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 380
          }} data-id="element-71">
              <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1516490981167-dc990a242afe?w=600&q=80')"
            }} data-id="element-72" />
              <div className="absolute inset-0" style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.72) 100%)'
            }} data-id="element-73" />
              <div className="absolute top-4 right-5 font-bold select-none" style={{
              fontSize: '4.5rem',
              lineHeight: 1,
              color: 'rgba(255,255,255,0.22)'
            }} data-id="element-74">
                04
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-75">
                <p className="text-lg font-bold text-white mb-1" data-id="element-76">Chaosas</p>
                <p className="text-sm text-white/80 leading-relaxed" data-id="element-77">
                  Ką daryti su emocijomis sudėtingose situacijose?
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── TARP NUOLATINĖS KAITOS ── */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-78">
        <div className="max-w-6xl mx-auto" data-id="element-79">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-80">
            <p className="font-accent italic text-gold-light mb-4 text-[28px]" data-id="element-81">
              Paieškos
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-82" />
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight" data-id="element-83">
              Tarp nuolatinės kaitos ir pusiausvyros paieškų
            </h2>
          </motion.div>

          <motion.div className="flex flex-col gap-12 items-center" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-84">
            <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto" data-id="element-85">
              <p className="text-xl md:text-2xl text-white/80 leading-relaxed font-[200]" data-id="element-86">
                Šiandienos pasaulis sukasi neregėtu tempu. Informacijos srautas,
                nuolatiniai reikalavimai ir pokyčiai kuria vidinę įtampą, kuri
                laikui bėgant virsta stresu, perdegimo jausmu ir prarastu ryšiu
                su savimi.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="w-full max-w-2xl mx-auto space-y-0" data-id="element-87">
              {['Kaip efektyviai spręsti problemas neprarandant vidinės ramybės?', 'Kaip giliai atsipalaiduoti ir tikrai pailsėti?', 'Kaip pasiekti gyvenime harmoniją ir pilnatvę?', 'Kaip valdyti emocijas ir dėmesį sudėtingose situacijose?'].map((question, i) => <div key={i} className="flex items-start gap-5 py-6" style={{
              borderBottom: i < 3 ? '1px solid rgba(255,255,255,0.1)' : 'none'
            }} data-id="element-88">
                  <span className="font-bold text-gold-light flex-shrink-0" style={{
                fontSize: '2rem',
                lineHeight: 1.1
              }} data-id="element-89">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-white/80 text-lg leading-relaxed pt-0.5" data-id="element-90">
                    {question}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── WHAT IS DFB ── */}
      <section id="metodika" className="relative overflow-hidden" style={{
      minHeight: 600
    }} data-id="element-91">
        {/* Full-width background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1920&q=80')"
      }} data-id="element-92" />
        {/* Left-to-right cinematic gradient */}
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(to right, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.72) 40%, rgba(0,0,0,0.25) 70%, transparent 100%)'
      }} data-id="element-93" />

        {/* Content — left half */}
        <motion.div className="relative z-10 flex items-center" style={{
        minHeight: 600
      }} {...fadeInUp} data-id="element-94">
          <div className="max-w-7xl mx-auto w-full px-8 md:px-16 py-24" data-id="element-95">
            <div className="max-w-xl" data-id="element-96">
              {/* Italic subtitle — now above */}
              <p className="font-accent italic text-gold-light mb-4 text-[28px]" data-id="element-97">
                Sprendimas
              </p>
              {/* Gold line */}
              <div className="h-[2px] w-[160px] mb-4 rounded-full" style={{
              background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
            }} data-id="element-98" />
              {/* Section heading */}
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight" data-id="element-99">
                Sąmoningas Būsenų Valdymas
              </h2>
              {/* Paragraphs */}
              <p className="text-lg text-white/80 leading-relaxed mb-5 font-[300]" data-id="element-100">
                Diferencijuotos Funkcinės Būsenos – tai aktyvios
                savireguliacijos metodika, leidžianti sąmoningai valdyti vidines
                būsenas nepriklausomai nuo išorinės veiklos.
              </p>
              <p className="text-lg text-white/80 leading-relaxed font-[300]" data-id="element-101">
                Metodika unikali tuo, kad moko ne slopinti emocijas, o jas
                diferencijuoti, sąmoningai pasirinkti ir pritaikyti.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ── 16 STATES ── (dark bg) */}
      <section className="py-24 px-4 bg-mountain-darker text-white" data-id="element-102">
        <div className="max-w-6xl mx-auto" data-id="element-103">
          <motion.div className="text-center mb-12" {...fadeInUp} data-id="element-104">
            <p className="font-accent italic text-gold-light mb-4 text-[28px]" data-id="element-105">
              Aktyvios Savireguliacijos Sistema
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-106" />
            <h3 className="text-3xl md:text-4xl font-bold text-white" data-id="element-107">
              16 Funkcinių Būsenų
            </h3>
          </motion.div>

          <motion.div className="mb-16 max-w-3xl mx-auto" {...fadeInUp} data-id="element-108">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-2xl text-center" data-id="element-109">
              <p className="text-white/80 leading-relaxed" data-id="element-110">
                DFB metodika dalinasi į žvalgą: kiekvienai veiklai reikalinga
                skirtinga psichikos būsena. Trumpų distancijų bėgimas reikalauja
                kitokio pasirengimo nei maratonas. Lengvas pokalbis – tai viena
                būsena, o intensyvus protinis darbas – visai kita.
              </p>
            </div>
          </motion.div>

          {/* Vertical Timeline Flow */}
          <motion.div className="relative max-w-4xl mx-auto py-8" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-111">
            {/* Central Vertical Line */}
            <div className="absolute left-8 md:left-1/2 top-12 bottom-12 w-0.5 bg-gradient-to-b from-gold/60 via-gold/40 to-gold/20 -translate-x-1/2" data-id="element-112" />

            <div className="flex flex-col gap-16 md:gap-20 relative z-10" data-id="element-113">
              {[{
              title: '4 Mentalinio Aktyvumo Būsenos',
              desc: 'Nuo chaoso iki tikslaus mąstymo',
              img: 'https://cdn.magicpatterns.com/uploads/mQgHHhWuza3DH22RUJu9Km/buddha-elemental-3d-tAmrB4ivdzQ-unsplash.jpg'
            }, {
              title: '4 Emocinio Aktyvumo Būsenos',
              desc: 'Nuo gilumo ir jautrumo iki lengvumo ir dinamiškumo bendraujant',
              img: 'https://cdn.magicpatterns.com/uploads/sd7vBtnHMeeuq1X4h2CTA7/Screenshot_2026-03-06_at_09.58.24.png'
            }, {
              title: '4 Fizinio Aktyvumo Būsenos',
              desc: 'Nuo plaučių, erdivinių judėsių iki tikslingo, koordinuotos veiklos',
              img: 'https://images.unsplash.com/photo-1545389336-cf090694435e?w=200&q=80'
            }, {
              title: '4 Harmoningo Aktyvumo Būsenos',
              desc: 'Kūnas, emocijos ir protas veikia darniai kaip viena visuma',
              img: 'https://cdn.magicpatterns.com/uploads/9pNg9YiKu6QqoEs7QRi3yW/johannes-plenio-Enhs8UrXEb0-unsplash.jpg'
            }].map((item, idx) => {
              const isEven = idx % 2 === 0;
              return <motion.div key={idx} variants={fadeInUp} className={`relative flex flex-col md:flex-row items-start md:items-center w-full ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`} data-id="element-114">
                    {/* Mobile Dot */}
                    <div className="md:hidden absolute left-8 top-12 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold ring-4 ring-mountain-darker z-20" data-id="element-115" />

                    {/* Desktop Center Dot */}
                    <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-gold ring-4 ring-mountain-darker z-20" data-id="element-116" />

                    {/* Image Side */}
                    <div className={`w-full md:w-1/2 flex pl-20 md:pl-0 ${isEven ? 'md:justify-end md:pr-16' : 'md:justify-start md:pl-16'} mb-5 md:mb-0`} data-id="element-117">
                      <div className="relative z-10 w-24 h-24 rounded-full overflow-hidden border-4 border-gold ring-2 ring-gold/25 flex-shrink-0 shadow-xl" data-id="element-118">
                        <div className="w-full h-full bg-cover bg-center" style={{
                      backgroundImage: `url('${item.img}')`
                    }} data-id="element-119" />
                      </div>
                    </div>

                    {/* Text Side */}
                    <div className={`w-full md:w-1/2 flex flex-col pl-20 md:pl-0 ${isEven ? 'md:items-start md:pl-16' : 'md:items-end md:pr-16 md:text-right'}`} data-id="element-120">
                      <h4 className="text-lg font-bold text-white mb-2 leading-snug" data-id="element-121">
                        {item.title}
                      </h4>
                      <p className="text-sm text-white/60 leading-relaxed max-w-sm" data-id="element-122">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── BENEFITS ── (light bg) */}
      <section className="py-24 px-4 bg-sand-lightest" data-id="element-123">
        <div className="max-w-5xl mx-auto" data-id="element-124">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-125">
            <p className="font-accent italic text-gold-dark mb-4 text-[28px]" data-id="element-126">
              Rezultatai
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-127" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-128">
              DFB Metodologijos Nauda
            </h2>
          </motion.div>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-129">
            {/* First row: 3 items */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 mb-12" data-id="element-130">
              {[{
              icon: <HeartIcon className="w-12 h-12 text-gold-dark" data-id="element-131" />,
              title: 'Nuolatinė Ramybė',
              desc: 'Stabilumas nepriklausomai nuo išorinio pasaulio'
            }, {
              icon: <BrainIcon className="w-12 h-12 text-gold-dark" data-id="element-132" />,
              title: 'Dėmesio Valdymas',
              desc: 'Sąmoningas dėmesio nukreipimas į svarbius dalykus'
            }, {
              icon: <SparklesIcon className="w-12 h-12 text-gold-dark" data-id="element-133" />,
              title: 'Aktyvi Savireguliacija',
              desc: 'Efektyvus streso ir emocijų valdymas'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-134">
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-135" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-136">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-137">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>

            {/* Second row: 2 items centered */}
            <div className="flex flex-col sm:flex-row justify-center gap-12" data-id="element-138">
              {[{
              icon: <Users className="w-12 h-12 text-gold-dark" data-id="element-139" />,
              title: 'Emocinis Intelektas',
              desc: 'Geresnis savęs ir kitų supratimas'
            }, {
              icon: <SunIcon className="w-12 h-12 text-gold-dark" data-id="element-140" />,
              title: 'Giluminis Poilsis',
              desc: 'Efektyvus atsigavimas per trumpą laiką'
            }].map((benefit, i) => <motion.div key={i} variants={fadeInUp} className="flex flex-col items-center text-center sm:w-[calc(33.333%-1.5rem)]" data-id="element-141">
                  {benefit.icon}
                  <div className="w-8 h-px bg-gold mx-auto my-4" data-id="element-142" />
                  <h3 className="text-xl font-bold text-mountain-darker mb-2" data-id="element-143">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-mountain-dark/60 max-w-[180px] mx-auto leading-relaxed" data-id="element-144">
                    {benefit.desc}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE OFFER ── (dark bg) */}
      <section id="registracija" className="py-24 px-4 bg-mountain-darker text-white" data-id="element-145">
        <div className="max-w-5xl mx-auto" data-id="element-146">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-147">
            <p className="font-accent italic text-gold-light mb-4 text-[28px]" data-id="element-148">
              DFB Metodika: Medituoti Lengva
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-149" />
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-150">
              Prisijunk Prie Kurso
            </h2>
          </motion.div>

          <motion.div className="flex flex-col sm:flex-row items-stretch justify-center mb-12" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-151">
            {[{
            value: '90',
            label: 'Akademinių valandų',
            sub: '30 užsiėmimų po 2–2.5 val.'
          }, {
            value: '2.5',
            label: 'Mėnesiai',
            sub: 'Programos trukmė'
          }, {
            value: '10',
            label: 'Dalyvių max',
            sub: 'Optimali grupė'
          }, {
            value: '330€',
            label: '110€ už mėnesį',
            sub: 'Galimybė mokėti dalimis'
          }].map((stat, i, arr) => <motion.div key={stat.value} variants={fadeInUp} className="flex-1 flex flex-col items-center text-center px-8 py-8 relative" data-id="element-152">
                {i < arr.length - 1 && <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2" style={{
              width: 1,
              height: '60%',
              background: 'rgba(255,255,255,0.12)'
            }} data-id="element-153" />}
                <span className="font-bold text-gold-light leading-none mb-3" style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)'
            }} data-id="element-154">
                  {stat.value}
                </span>
                <p className="text-sm font-semibold text-white uppercase tracking-wide mb-1" data-id="element-155">
                  {stat.label}
                </p>
                <p className="text-xs text-white/50 leading-relaxed" data-id="element-156">
                  {stat.sub}
                </p>
              </motion.div>)}
          </motion.div>

          <div className="w-full h-px mb-12" style={{
          background: 'rgba(255,255,255,0.1)'
        }} data-id="element-157" />

          <motion.div className="text-center" {...fadeInUp} data-id="element-158">
            <motion.a href={REGISTRATION_URL} target="_blank" rel="noopener noreferrer" whileHover={{
            backgroundColor: 'rgb(var(--gold))',
            color: 'rgb(var(--mountain-darker))',
            scale: 1.05,
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.5)'
          }} whileTap={{
            scale: 0.97
          }} className="inline-flex items-center gap-2 whitespace-nowrap bg-white text-mountain-darker px-12 py-[18px] rounded-full text-lg font-semibold shadow-2xl transition-colors mb-5" data-id="element-159">
              Rezervuok kursą
              <ArrowRightIcon className="w-5 h-5 flex-shrink-0" data-id="element-160" />
            </motion.a>
            <div className="flex items-center justify-center gap-2 mt-3" data-id="element-161">
              <span className="inline-block w-2 h-2 rounded-full flex-shrink-0 bg-red-400" data-id="element-162" />
              <p className="text-sm text-white/50" data-id="element-163">
                Vietos ribotos – užsiregistruok jau šiandien
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── COURSE STRUCTURE ── (light bg) */}
      <section id="programa" className="py-24 px-4 bg-sand-light" data-id="element-164">
        <div className="max-w-6xl mx-auto" data-id="element-165">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-166">
            <p className="font-accent italic text-gold-dark mb-4 text-[28px]" data-id="element-167">
              3 etapai: išmok, pajusk, pritaikyk
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-168" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-169">
              Kurso Struktūra
            </h2>
          </motion.div>

          <motion.div className="space-y-4" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-170">
            {[{
            num: '01',
            title: 'Pirmoji Dalis: Pagrindai',
            desc: 'Baziniai pratimai ir teoriniai pagrindai',
            img: 'https://images.unsplash.com/photo-1712342109846-a8fcb1c883ba?w=1200&q=80'
          }, {
            num: '02',
            title: 'Antroji Dalis: Būsenos',
            desc: '16 aktyvumo būsenų atpažinimas ir valdymas',
            img: 'https://images.unsplash.com/photo-1703945530393-600961291ced?w=1200&q=80'
          }, {
            num: '03',
            title: 'Trečioji Dalis: Integracija',
            desc: 'Įgūdžių taikymas kasdieniame gyvenime',
            img: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=1200&q=80'
          }].map(part => <motion.div key={part.num} variants={fadeInUp} className="relative rounded-2xl overflow-hidden" style={{
            minHeight: 200
          }} data-id="element-171">
                {/* Background image */}
                <div className="absolute inset-0 bg-cover bg-center" style={{
              backgroundImage: `url('${part.img}')`
            }} data-id="element-172" />
                {/* Left-to-right dark gradient */}
                <div className="absolute inset-0" style={{
              background: 'linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0.1) 100%)'
            }} data-id="element-173" />
                {/* Ghost number — far right */}
                <div className="absolute right-8 top-1/2 -translate-y-1/2 font-bold select-none leading-none text-white" style={{
              fontSize: '8rem',
              opacity: 0.1
            }} aria-hidden="true" data-id="element-174">
                  {part.num}
                </div>
                {/* Content — left side */}
                <div className="relative z-10 flex items-center gap-6 px-10 py-10" data-id="element-175">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/60 flex items-center justify-center" data-id="element-176">
                    <span className="text-gold-light font-bold text-sm tracking-widest" data-id="element-177">
                      {part.num}
                    </span>
                  </div>
                  <div data-id="element-178">
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-1 leading-tight" data-id="element-179">
                      {part.title}
                    </h3>
                    <p className="text-white/70 text-base md:text-lg leading-relaxed" data-id="element-180">
                      {part.desc}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── (light bg) */}
      <section className="py-24 px-4 bg-sand-lightest overflow-hidden" data-id="element-181">
        <div className="max-w-6xl mx-auto" data-id="element-182">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-183">
            <p className="font-accent italic text-gold-dark mb-4 text-[28px]" data-id="element-184">
              Tikros transformacijos istorijos
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-185" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-186">
              Dalyvių Atsiliepimai
            </h2>
          </motion.div>

          {(() => {
          const testimonials = [{
            name: 'Gabrielė J.',
            role: 'Kursų dalyvė',
            img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=80',
            quote: 'Nuo pirmos dienos jaučiau, kad patekau į saugią ir gerai apgalvotą mokymosi struktūrą, bet kartu apsupta labai daug šilumos ir palaikymo. DFB padėjo man atpažinti ir naviguoti neramius jausmus ir chaotiškas būsenas, bei juos keisti. Sugrįžti į čia ir dabar. Prisiimti atsakomybę už savo dėmesį. Nuosekliai kartojant pratimus ir darant praktikas tikrai vyko pozityvus pokytis.'
          }, {
            name: 'Jonas P.',
            role: 'Kursų dalyvis',
            img: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=80',
            quote: 'Pagrindiniai įsisamoninimai ir suvokimai atėję nuoširdžiai praktikuojant ir naudojant DFB metodą yra:\n\nišmokimas, kaip keisti savo būsenas ir stebėti kaip per tai keičiasi ir aplinka, žmonės bei jų reakcijos.\n\nSuvokimas, kaip įgalinti savo naudai visas būsenas neskirtinant jų į geras ir blogas. Taika su pasauliu.'
          }, {
            name: 'Laura K.',
            role: 'Kursų dalyvė',
            img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80',
            quote: 'Labiausiai patiko, kad 99% mokymų buvo patyrimas ir pajautimas. Suvokimas visko per save ir per kitus. Būtent todėl DFB lengvai integravosi į mano gyvenimą.\n\n\nPaskaičius ne vieną aprašymą apie DFB internete, jau besimokant, niekaip nesugalvočiau mokytis, nes visiškai neatspindi to, ką gauni. O gauni be galo daug. Sakyčiau, kad DFB - didelis asmeninis virsmas.'
          }, {
            name: 'Tomas N.',
            role: 'Kursų dalyvis',
            img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&q=80',
            quote: 'Labai aiškiai ir struktūruotai perteikiama kurso informacija, palydima per patyrimus. Isiklausymas į tai, kas kiekvienam aktualu, koks kiekvieno mokinio iššūkis, ir atsižvelgimas į tai. Rūpestis erdvėmis, atmosfera, lankstumas. Nuolatinis palaikymas, domėjimasis, įkvėpimas per konkrečius pavyzdžius. Dėkui už transformaciją ir nepakartojama pokytį.'
          }, {
            name: 'Eglė M.',
            role: 'Kursų dalyvė',
            img: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&q=80',
            quote: 'Nuo pirmos dienos jaučiau, kad patekau į saugią ir gerai apgalvotą mokymosi struktūrą, bet kartu apsupta labai daug šilumos ir palaikymo. DFB padėjo man atpažinti ir naviguoti neramius jausmus.'
          }, {
            name: 'Marius V.',
            role: 'Kursų dalyvis',
            img: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&q=80',
            quote: 'Nuosekliai kartojant pratimus ir darant praktikas tikrai jautėsi pozityvus pokytis. Sugrįžti į čia ir dabar tapo daug paprasčiau. Prisiimti atsakomybę už savo dėmesį – geriausia pamoka.'
          }];
          const [activeIdx, setActiveIdx] = useState(0);
          const [direction, setDirection] = useState(0);
          const goTo = (idx: number) => {
            setDirection(idx > activeIdx ? 1 : -1);
            setActiveIdx(idx);
          };
          const prev = () => goTo(activeIdx === 0 ? testimonials.length - 1 : activeIdx - 1);
          const next = () => goTo(activeIdx === testimonials.length - 1 ? 0 : activeIdx + 1);
          const t = testimonials[activeIdx];
          const slideVariants = {
            enter: (dir: number) => ({
              opacity: 0,
              x: dir * 60
            }),
            center: {
              opacity: 1,
              x: 0
            },
            exit: (dir: number) => ({
              opacity: 0,
              x: dir * -60
            })
          };
          return <div className="relative flex flex-col items-center" data-id="element-187">
                <div className="flex items-center w-full gap-4 md:gap-8" data-id="element-188">
                  {/* Left arrow */}
                  <button onClick={prev} aria-label="Ankstesnis" className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-189">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-190">
                      <polyline points="15 18 9 12 15 6" data-id="element-191" />
                    </svg>
                  </button>

                  {/* Testimonial content */}
                  <div className="flex-1 min-w-0 overflow-hidden" data-id="element-192">
                    <AnimatePresence mode="wait" custom={direction} data-id="element-193">
                      <motion.div key={activeIdx} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.45,
                    ease: 'easeInOut'
                  }} className="flex flex-col items-center text-center px-2 md:px-8" data-id="element-194">
                        {/* Decorative quote mark */}
                        <span className="font-serif text-gold/25 select-none leading-none mb-2" style={{
                      fontSize: '6rem',
                      lineHeight: 0.8
                    }} aria-hidden="true" data-id="element-195">
                          &ldquo;
                        </span>

                        {/* Quote text */}
                        <p className="text-mountain-darker text-lg md:text-xl leading-relaxed whitespace-pre-line max-w-2xl mx-auto mb-10 font-[400]" data-id="element-196">
                          {t.quote}
                        </p>

                        {/* Gold divider */}
                        <div className="w-12 h-px bg-gold mb-4" data-id="element-197" />

                        {/* Name & role */}
                        <p className="font-bold text-mountain-darker text-lg" data-id="element-198">
                          {t.name}
                        </p>
                        <p className="text-sm text-mountain-dark/60 mt-0.5" data-id="element-199">
                          {t.role}
                        </p>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Right arrow */}
                  <button onClick={next} aria-label="Kitas" className="flex-shrink-0 w-12 h-12 rounded-full border-2 border-gold/40 bg-white hover:bg-gold hover:border-gold text-gold-dark hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm" data-id="element-200">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-201">
                      <polyline points="9 18 15 12 9 6" data-id="element-202" />
                    </svg>
                  </button>
                </div>

                {/* Dot indicators */}
                <div className="flex items-center gap-3 mt-10" data-id="element-203">
                  {testimonials.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Atsiliepimas ${i + 1}`} className="transition-all duration-300 rounded-full" style={{
                width: i === activeIdx ? '2rem' : '0.5rem',
                height: '0.5rem',
                background: i === activeIdx ? 'rgb(var(--gold))' : 'rgba(var(--gold), 0.3)'
              }} data-id="element-204" />)}
                </div>
              </div>;
        })()}
        </div>
      </section>

      {/* ── TEAM ── (dark bg) */}
      <section id="komanda" className="py-32 px-4 bg-mountain-darker text-white" data-id="element-205">
        <div className="max-w-4xl mx-auto" data-id="element-206">
          <motion.div className="text-center mb-20" {...fadeInUp} data-id="element-207">
            <p className="font-accent italic text-gold-light mb-4 text-[28px]" data-id="element-208">
              Patyrę instruktoriai ir praktikai
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-209" />
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-210">
              Mūsų Komanda
            </h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-20" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true,
          margin: '-80px'
        }} data-id="element-211">
            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-212">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-213">
                <div className="absolute inset-0 bg-cover bg-center" style={{
                backgroundImage: "url('https://cdn.magicpatterns.com/uploads/i1o1k6S9HKsnepCQJbAGMr/Agne.png')",
                backgroundSize: '75%',
                backgroundRepeat: 'no-repeat'
              }} data-id="element-214" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-215">
                Agnė Kubertė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-216">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-217" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-218">
                Daugiau nei 10 metų patirtis savireguliacijos ir mindfulness
                srityje. Veda grupinius ir individualius užsiėmimus, padedančius
                žmonėms atrasti vidinę pusiausvyrą.
              </p>
              <div className="relative max-w-xs" data-id="element-219">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-220">
                  "
                </span>
                <p className="font-accent italic text-white/75 text-lg leading-relaxed pl-4" data-id="element-221">
                  Kiekvienas žmogus turi savyje resursų – mano darbas padėti
                  juos atrasti.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-col items-center text-center" data-id="element-222">
              <div className="relative w-52 h-52 mx-auto mb-8 rounded-full overflow-hidden ring-2 ring-gold ring-offset-8 ring-offset-mountain-darker flex-shrink-0" data-id="element-223">
                <div className="absolute inset-0 bg-cover" style={{
                backgroundImage: "url('https://cdn.magicpatterns.com/uploads/fZddL3LU6BReBnFqKvzTWW/vestina.jpg')",
                backgroundPosition: 'center 45%'
              }} data-id="element-224" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-1" data-id="element-225">
                Vestina Zablockyė
              </h3>
              <p className="text-sm uppercase tracking-widest text-gold-light font-semibold mb-6" data-id="element-226">
                DFB metodologijos Instruktorė
              </p>
              <div className="w-12 h-px bg-gold/40 mb-6" data-id="element-227" />
              <p className="text-white/70 leading-relaxed mb-8 max-w-xs" data-id="element-228">
                Specializuojasi emocinio intelekto ugdyme ir streso valdyme. Jos
                praktinis požiūris padeda dalyviams greitai integruoti DFB
                technikas į kasdienį gyvenimą.
              </p>
              <div className="relative max-w-xs" data-id="element-229">
                <span className="absolute -top-4 -left-2 font-serif text-gold/30 select-none leading-none" style={{
                fontSize: '4rem',
                lineHeight: 1
              }} aria-hidden="true" data-id="element-230">
                  "
                </span>
                <p className="font-accent italic text-white/75 text-lg leading-relaxed pl-4" data-id="element-231">
                  Sąmoningumas – tai ne tikslas, o kelias, kuriuo einame
                  kiekvieną dieną.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SCHEDULE ── (light bg) */}
      <section id="tvarkarastis" className="py-24 px-4 bg-sand-lightest" data-id="element-232">
        <div className="max-w-6xl mx-auto" data-id="element-233">
          <motion.div className="text-center mb-16" {...fadeInUp} data-id="element-234">
            <p className="font-accent italic text-gold-dark mb-4 text-[28px]" data-id="element-235">
              Prisijunk prie pavasario grupės
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-dark)), transparent)'
          }} data-id="element-236" />
            <h2 className="text-4xl md:text-5xl font-bold text-mountain-darker" data-id="element-237">
              Artėjantis Kursas
            </h2>
          </motion.div>

          <motion.div {...fadeInUp} className="space-y-10" data-id="element-238">
            {/* Date + days — unified card */}
            <div className="bg-white border border-sand-dark/20 rounded-2xl px-8 py-6 max-w-2xl mx-auto" data-id="element-239">
              <div className="flex items-center gap-3 justify-center mb-5" data-id="element-240">
                <CalendarIcon className="w-5 h-5 text-gold-dark flex-shrink-0" data-id="element-241" />
                <p className="text-2xl font-bold text-mountain-darker" data-id="element-242">
                  Kovo 16 – Gegužės 21
                </p>
              </div>
              <div className="w-12 h-px bg-gold mx-auto mb-5" data-id="element-243" />
              <div className="flex flex-wrap justify-center gap-2" data-id="element-244">
                {['Pirmadienis', 'Antradienis', 'Ketvirtadienis'].map(day => <div key={day} className="flex items-center gap-2 bg-sand-lightest border border-sand-dark/20 px-4 py-2 rounded-xl" data-id="element-245">
                    <div className="w-1.5 h-1.5 bg-gold rounded-full" data-id="element-246" />
                    <span className="text-mountain-darker font-medium text-sm" data-id="element-247">
                      {day}
                    </span>
                  </div>)}
              </div>
            </div>

            <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
            once: true,
            margin: '-80px'
          }} data-id="element-248">
              <motion.div variants={fadeInUp} className="bg-white border border-sand-dark/30 p-5 rounded-2xl" data-id="element-249">
                <h3 className="text-base font-bold text-mountain-darker mb-4 text-center tracking-wide" data-id="element-250">
                  Kovas 2026
                </h3>
                <div className="grid grid-cols-7 gap-1" data-id="element-251">
                  {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map(d => <div key={d} className="text-center text-[10px] text-mountain-dark/40 font-semibold pb-1" data-id="element-252">
                      {d}
                    </div>)}
                  {[...Array(6)].map((_, i) => <div key={`e-${i}`} className="aspect-square" data-id="element-253" />)}
                  {[...Array(31)].map((_, i) => {
                  const day = i + 1;
                  const dow = (i + 6) % 7;
                  const active = (dow === 0 || dow === 1 || dow === 3) && day >= 16;
                  return <div key={day} className={`aspect-square flex items-center justify-center rounded text-[11px] font-medium ${active ? 'bg-gold text-white font-bold' : 'text-mountain-dark/50'}`} data-id="element-254">
                        {day}
                      </div>;
                })}
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-white border border-sand-dark/30 p-5 rounded-2xl" data-id="element-255">
                <h3 className="text-base font-bold text-mountain-darker mb-4 text-center tracking-wide" data-id="element-256">
                  Balandis 2026
                </h3>
                <div className="grid grid-cols-7 gap-1" data-id="element-257">
                  {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map(d => <div key={d} className="text-center text-[10px] text-mountain-dark/40 font-semibold pb-1" data-id="element-258">
                      {d}
                    </div>)}
                  {[...Array(2)].map((_, i) => <div key={`e-${i}`} className="aspect-square" data-id="element-259" />)}
                  {[...Array(30)].map((_, i) => {
                  const day = i + 1;
                  const dow = (i + 2) % 7;
                  const active = dow === 0 || dow === 1 || dow === 3;
                  return <div key={day} className={`aspect-square flex items-center justify-center rounded text-[11px] font-medium ${active ? 'bg-gold text-white font-bold' : 'text-mountain-dark/50'}`} data-id="element-260">
                        {day}
                      </div>;
                })}
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-white border border-sand-dark/30 p-5 rounded-2xl" data-id="element-261">
                <h3 className="text-base font-bold text-mountain-darker mb-4 text-center tracking-wide" data-id="element-262">
                  Gegužė 2026
                </h3>
                <div className="grid grid-cols-7 gap-1" data-id="element-263">
                  {['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'].map(d => <div key={d} className="text-center text-[10px] text-mountain-dark/40 font-semibold pb-1" data-id="element-264">
                      {d}
                    </div>)}
                  {[...Array(4)].map((_, i) => <div key={`e-${i}`} className="aspect-square" data-id="element-265" />)}
                  {[...Array(31)].map((_, i) => {
                  const day = i + 1;
                  const dow = (i + 4) % 7;
                  const active = (dow === 0 || dow === 1 || dow === 3) && day <= 21;
                  return <div key={day} className={`aspect-square flex items-center justify-center rounded text-[11px] font-medium ${active ? 'bg-gold text-white font-bold' : 'text-mountain-dark/50'}`} data-id="element-266">
                        {day}
                      </div>;
                })}
                </div>
              </motion.div>
            </motion.div>

            <motion.div className="flex flex-col sm:flex-row items-stretch justify-center border border-sand-dark/30 rounded-2xl bg-white overflow-hidden" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
            once: true,
            margin: '-80px'
          }} data-id="element-267">
              {[{
              value: '10',
              label: 'Savaičių',
              sub: 'Programos trukmė'
            }, {
              value: '30',
              label: 'Užsiėmimų',
              sub: 'Iš viso sesijų'
            }, {
              value: '90',
              label: 'Akademinių val.',
              sub: 'Po 2–2.5 val.'
            }].map((stat, i, arr) => <motion.div key={stat.value} variants={fadeInUp} className="flex-1 flex flex-col items-center text-center px-8 py-8 relative" data-id="element-268">
                  {i < arr.length - 1 && <div className="hidden sm:block absolute right-0 top-1/2 -translate-y-1/2" style={{
                width: 1,
                height: '55%',
                background: 'rgb(var(--sand-dark) / 0.4)'
              }} data-id="element-269" />}
                  <span className="font-bold text-gold-dark leading-none mb-2" style={{
                fontSize: 'clamp(2.5rem, 5vw, 3.5rem)'
              }} data-id="element-270">
                    {stat.value}
                  </span>
                  <p className="text-sm font-semibold text-mountain-darker uppercase tracking-wide mb-1" data-id="element-271">
                    {stat.label}
                  </p>
                  <p className="text-xs text-mountain-dark/50" data-id="element-272">{stat.sub}</p>
                </motion.div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA BANNER ── (dark bg) */}
      <section className="py-16 px-4 bg-sand-lightest" data-id="element-273">
        <motion.div className="relative max-w-6xl mx-auto min-h-[420px] rounded-3xl overflow-hidden flex items-center justify-center" {...fadeInUp} data-id="element-274">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url('${HERO_MOUNTAIN_IMAGE}')`
        }} data-id="element-275" />
          <div className="absolute inset-0" style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0.78) 100%)'
        }} data-id="element-276" />
          <div className="relative z-10 text-center px-6 py-16 flex flex-col items-center" data-id="element-277">
            <p className="font-accent italic text-gold-light mb-4 text-[28px]" data-id="element-278">
              Būtent čia slepiasi tavo laimė
            </p>
            <div className="h-[2px] w-[160px] mx-auto mb-4 rounded-full" style={{
            background: 'linear-gradient(to right, transparent, rgb(var(--gold-light)), transparent)'
          }} data-id="element-279" />
            <h2 className="text-4xl md:text-5xl text-white mb-10 leading-tight font-[700]" data-id="element-280">
              Tavo Vidinė Būsena Kuria Tavo Dieną
            </h2>
            <motion.button whileHover={{
            backgroundColor: 'rgb(var(--gold))',
            color: 'rgb(var(--mountain-darker))',
            scale: 1.05,
            boxShadow: '0 20px 50px -10px rgba(0,0,0,0.5)'
          }} whileTap={{
            scale: 0.97
          }} className="bg-white text-mountain-darker px-12 py-[18px] rounded-full text-lg font-semibold shadow-2xl transition-colors inline-flex items-center gap-2" data-id="element-281">
              Rezervuok kursą
              <ArrowRightIcon className="w-5 h-5 flex-shrink-0" data-id="element-282" />
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-12 px-4 bg-sand-lightest border-t border-sand-dark/20" data-id="element-283">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-284">
          <div className="flex items-center gap-2" data-id="element-285">
            <span className="text-gold-dark font-accent text-xl tracking-wide" data-id="element-286">
              medituoti lengva
            </span>
          </div>
          <p className="text-mountain-dark/50 text-sm" data-id="element-287">
            © 2026 medituoti lengva. Visos teisės saugomos.
          </p>
          <div className="flex items-center gap-6" data-id="element-288">
            <a href="#apie" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-289">
              Apie
            </a>
            <a href="#metodika" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-290">
              Metodika
            </a>
            <a href="#registracija" className="text-mountain-dark/50 hover:text-mountain-darker text-sm transition-colors" data-id="element-291">
              Registracija
            </a>
          </div>
        </div>
      </footer>
    </main>;
}